(ns yardwerkz.web.domain.jobs
  "Pure functions for jobs list business logic.
   All functions are testable without React or RFx.")

;; =============================================================================
;; Status Configuration
;; =============================================================================

(def status-configs
  "Map of job statuses to their display configuration."
  {:draft {:label "Draft" :color-key [:gray :500] :bg-key [:gray :100] :icon "pencil-outline"}
   :pending {:label "Pending" :color-key [:amber :500] :bg-key [:amber :100] :icon "clock-outline"}
   :submitted {:label "Awaiting Quotes" :color-key [:amber :500] :bg-key [:amber :100] :icon "clock-outline"}
   :matching {:label "Finding a Worker" :color-key [:blue :500] :bg-key [:blue :100] :icon "account-search-outline"}
   :quoted {:label "Quotes Available" :color-key [:blue :500] :bg-key [:blue :100] :icon "comment-text-multiple-outline"}
   :scheduled {:label "Scheduled" :color-key [:blue :500] :bg-key [:blue :100] :icon "calendar-check"}
   :assigned {:label "Awaiting Payment" :color-key [:blue :500] :bg-key [:blue :100] :icon "credit-card-clock-outline"}
   :accepted {:label "Awaiting Payment" :color-key [:blue :500] :bg-key [:blue :100] :icon "credit-card-clock-outline"}
   :paid-for {:label "Paid - Ready to Start" :color-key [:green :600] :bg-key [:green :100] :icon "check-decagram"}
   :paid_for {:label "Paid - Ready to Start" :color-key [:green :600] :bg-key [:green :100] :icon "check-decagram"}
   :paid {:label "Paid - Ready to Start" :color-key [:green :600] :bg-key [:green :100] :icon "check-decagram"}
   :en-route {:label "Worker En Route" :color-key [:violet :500] :bg-key [:violet :100] :icon "car"}
   :en_route {:label "Worker En Route" :color-key [:violet :500] :bg-key [:violet :100] :icon "car"}
   :started {:label "In Progress" :color-key [:violet :500] :bg-key [:violet :100] :icon "progress-wrench"}
   :in-progress {:label "In Progress" :color-key [:violet :500] :bg-key [:violet :100] :icon "progress-wrench"}
   :finished {:label "Review Work" :color-key [:amber :500] :bg-key [:amber :100] :icon "clipboard-check-outline"}
   :completed {:label "Completed" :color-key [:green :600] :bg-key [:green :100] :icon "check-circle"}
   :expired-no-quotes {:label "Expired" :color-key [:gray :500] :bg-key [:gray :100] :icon "clock-alert-outline"}
   :cancelled {:label "Cancelled" :color-key [:red :500] :bg-key [:red :100] :icon "close-circle-outline"}
   :canceled {:label "Cancelled" :color-key [:red :500] :bg-key [:red :100] :icon "close-circle-outline"}
   :disputed {:label "Dispute Open" :color-key [:red :600] :bg-key [:red :100] :icon "alert-circle"}
   :resolved {:label "Dispute Resolved" :color-key [:green :600] :bg-key [:green :100] :icon "check-decagram"}})

(def default-status-config
  {:label "Unknown" :color-key [:gray :500] :bg-key [:gray :100] :icon "help-circle"})

(defn get-status-config
  "Get display configuration for a job status."
  [status]
  (get status-configs status default-status-config))

;; =============================================================================
;; Status Sets
;; =============================================================================

(def active-statuses
  "Set of statuses considered 'active' (job in progress).
   Includes both hyphenated (Datomic ident) and underscored (legacy) variants."
  #{:pending :draft :submitted :matching :quoted :scheduled :assigned
    :accepted :paid :paid-for :paid_for :en-route :en_route :started :in-progress
    :finished})

(def past-statuses
  "Set of statuses considered 'past' (job finished or cancelled)."
  #{:completed :cancelled :canceled :expired-no-quotes :closed :resolved})

(def cancellable-statuses
  "Set of statuses where job can be cancelled."
  #{:pending :submitted :matching :quoted :accepted :scheduled :assigned
    :paid :paid-for :paid_for :in-progress :started})

(def completed-statuses
  "Set of statuses indicating job is complete (customer-approved)."
  #{:completed})

(def cancelled-statuses
  "Set of statuses indicating job was cancelled."
  #{:cancelled :canceled})

(def accepted-statuses
  "Set of statuses indicating a quote was accepted."
  #{:accepted :assigned :scheduled})

;; =============================================================================
;; Status Checks
;; =============================================================================

(defn active?
  "Returns true if job status is active."
  [status]
  (contains? active-statuses status))

(defn past?
  "Returns true if job status is past."
  [status]
  (contains? past-statuses status))

(defn completed?
  "Returns true if job status is completed."
  [status]
  (contains? completed-statuses status))

(defn finished?
  "Returns true if job status is finished (awaiting customer review)."
  [status]
  (= status :finished))

(defn can-approve?
  "Returns true if customer can approve the finished work."
  [status has-worker?]
  (and (finished? status) has-worker?))

(defn en-route?
  "Returns true if worker is en route to the job site."
  [status]
  (contains? #{:en-route :en_route} status))

(defn cancelled?
  "Returns true if job status is cancelled."
  [status]
  (contains? cancelled-statuses status))

(defn can-cancel?
  "Returns true if job can be cancelled."
  [status is-cancelled?]
  (and (not (completed? status))
       (not is-cancelled?)
       (contains? cancellable-statuses status)))

(defn can-rate?
  "Returns true if customer can rate the worker."
  [status has-worker? has-rated?]
  (and (completed? status)
       has-worker?
       (not has-rated?)))

(defn quoted?
  "Returns true if job has quotes available."
  [status]
  (= status :quoted))

(defn has-quotes?
  "Returns true if job has quotes that can be viewed."
  [status accepted-quote]
  (and (quoted? status) (nil? accepted-quote)))

(defn needs-payment?
  "Returns true if job needs payment."
  [status is-paid?]
  (and (= status :assigned) (not is-paid?)))

(defn accepted?
  "Returns true if a quote was accepted."
  [status]
  (contains? accepted-statuses status))

;; =============================================================================
;; Date Formatting
;; =============================================================================

(def ^:private months
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn format-date
  "Format date to readable format like 'Dec 22, 2024'.
   Handles multiple input types:
   - String: ISO date like '2024-12-22' or '2024-12-22T...'
   - js/Date: #inst objects from Datomic
   - Temporal.PlainDate: from time-literals"
  [date-val]
  (when date-val
    (try
      (cond
        ;; Temporal.PlainDate - use its native properties
        (and (exists? js/Temporal)
             (instance? js/Temporal.PlainDate date-val))
        (let [month-idx (dec (.-month date-val))
              day (.-day date-val)
              year (.-year date-val)]
          (str (nth months month-idx) " " day ", " year))

        ;; js/Date (#inst from Datomic) - extract UTC date to avoid timezone shift
        (instance? js/Date date-val)
        (let [month-idx (.getUTCMonth date-val)
              day (.getUTCDate date-val)
              year (.getUTCFullYear date-val)]
          (str (nth months month-idx) " " day ", " year))

        ;; String - parse manually to avoid timezone issues
        (string? date-val)
        (if-let [[_ year month day] (re-matches #"(\d{4})-(\d{2})-(\d{2}).*" date-val)]
          (let [month-idx (dec (js/parseInt month 10))
                day-num (js/parseInt day 10)
                year-num (js/parseInt year 10)]
            (str (nth months month-idx) " " day-num ", " year-num))
          ;; Fallback to Date parsing for other string formats
          (let [date (js/Date. date-val)
                month (nth months (.getUTCMonth date))
                day (.getUTCDate date)
                year (.getUTCFullYear date)]
            (str month " " day ", " year)))

        :else (str date-val))
      (catch :default _
        (str date-val)))))

(defn format-quote-date
  "Format a quote's created-at date for display (relative time).
   Handles js/Date, Temporal.PlainDate, and string inputs."
  [date-val]
  (when date-val
    (try
      (let [;; Normalize to js/Date for time comparison
            date (cond
                   (instance? js/Date date-val) date-val
                   (and (exists? js/Temporal)
                        (instance? js/Temporal.PlainDate date-val))
                   (js/Date. (.toString date-val))
                   :else (js/Date. date-val))
            now (js/Date.)
            diff-ms (- now date)
            diff-days (js/Math.floor (/ diff-ms 86400000))]
        (cond
          (zero? diff-days) "Today"
          (= 1 diff-days) "Yesterday"
          (< diff-days 7) (str diff-days " days ago")
          :else (format-date date-val)))
      (catch :default _
        (str date-val)))))

;; =============================================================================
;; Price Formatting
;; =============================================================================

(defn format-price
  "Format price in cents to display string like '$75'."
  [price-cents]
  (when price-cents
    (str "$" (/ price-cents 100))))

;; =============================================================================
;; Job Transformation
;; =============================================================================

(def slot-labels
  "Human-readable labels for time slots."
  {"morning" "Morning (8am-12pm)"
   "afternoon" "Afternoon (12pm-5pm)"
   "evening" "Evening (5pm-8pm)"})

(defn format-slot
  "Format a slot keyword/string to display label."
  [slot]
  (get slot-labels (name slot) (name slot)))

(defn- extract-time-slot
  "Extract time slot keyword from enum. Handles both old and new formats."
  [slot]
  (cond
    (nil? slot) nil
    (map? slot) (when-let [ident (:db/ident slot)] (keyword (name ident)))
    (keyword? slot) (keyword (name slot))
    :else (keyword slot)))

(defn format-slot-preference
  "Format a single slot preference for display.
   Handles both old (:date/:slot) and new namespaced formats."
  [pref]
  (let [date (or (:job-slot-preference/date pref) (:date pref))
        raw-slot (or (:job-slot-preference/time-of-day pref) (:slot pref))
        slot (extract-time-slot raw-slot)
        priority (or (:job-slot-preference/priority pref) (:priority pref) 0)]
    {:date (format-date date)
     :slot (format-slot slot)
     :priority priority
     :short-slot (case (when slot (name slot))
                   "morning" "AM"
                   "afternoon" "PM"
                   "evening" "Eve"
                   (when slot (name slot)))}))

(defn format-slot-preferences
  "Format all slot preferences for display, sorted by priority."
  [prefs]
  (when (seq prefs)
    (->> prefs
         (sort-by :priority)
         (mapv format-slot-preference))))

(defn primary-schedule
  "Get the primary (first priority) schedule from slot preferences.
   Handles both old and new namespaced formats.
   Returns {:date \"Dec 25, 2024\" :time \"Morning\"} or nil."
  [slot-preferences]
  (when (seq slot-preferences)
    (let [;; Sort by priority (handle both namespaced and old format)
          sorted (sort-by #(or (:job-slot-preference/priority %) (:priority %) 0) slot-preferences)
          first-pref (first sorted)
          date (or (:job-slot-preference/date first-pref) (:date first-pref))
          raw-slot (or (:job-slot-preference/time-of-day first-pref) (:slot first-pref))
          slot (extract-time-slot raw-slot)]
      {:date (format-date date)
       :time (format-slot slot)})))

(defn- extract-status
  "Extract status keyword from job. Handles both old and new formats:
   - Old: {:status \"submitted\"}
   - New: {:job/status {:db/ident :job.status/submitted}}"
  [job]
  (if-let [ns-status (:job/status job)]
    ;; New format - extract from namespaced status
    (let [ident (if (map? ns-status) (:db/ident ns-status) ns-status)]
      (when ident (keyword (name ident))))
    ;; Old format - simple keyword conversion
    (keyword (:status job))))

(defn transform-job
  "Transform API job response to display format.
   Handles both old (unqualified) and new (namespaced) Datomic formats.
   Includes slot preferences if available, otherwise falls back to legacy fields."
  [job]
  (let [status (extract-status job)
        slot-prefs (or (:job/slot-preferences job) (:slot-preferences job))
        formatted-prefs (format-slot-preferences slot-prefs)
        ;; Use slot preferences if available, otherwise fall back to legacy fields
        primary (primary-schedule slot-prefs)
        date-display (or (:date primary)
                         (format-date (or (:job/preferred-date job) (:preferred-date job))))
        ;; Extract and format preferred time (handles enum format)
        raw-pref-time (or (:job/preferred-time job) (:preferred-time job))
        pref-time-slot (extract-time-slot raw-pref-time)
        time-display (or (:time primary)
                         (when pref-time-slot (format-slot pref-time-slot)))
        ;; Get service name from namespaced or unqualified format
        service-name (or (get-in job [:job/service :service/name])
                         (:service-name job)
                         "Service")
        ;; Get address from namespaced or unqualified format
        address (or (get-in job [:job/property :property/address])
                    (:address job)
                    "")]
    {:id (or (:job/id job) (:id job))
     :human-id (or (:job/human-id job) (:human-id job))
     :reference-id (or (:job/request-id job) (:reference-id job))
     :service service-name
     :address address
     :status status
     :date date-display
     :time time-display
     :price (format-price (or (:job/preferred-price job) (:preferred-price job)
                              (:job/quoted-price job) (:quoted-price job)))
     :worker (when-let [worker (or (:job/worker job) (:worker-name job))]
               {:name (if (map? worker)
                        (or (:worker/business-name worker)
                            (get-in worker [:worker/account :account/name])
                            "Pro")
                        worker)})
     ;; Keep raw created-at for sorting (js/Date or #inst)
     :created-at (or (:job/created-at job) (:created-at job))
     ;; Include all slot preferences for detailed display
     :slot-preferences formatted-prefs
     ;; Check if customer has rated (from reverse ref :rating/_job)
     :has-customer-rating?
     (boolean (some (fn [r]
                      (and (not (:rating/deleted? r))
                           (= :role/customer
                              (or (get-in r [:rating/rater-role :db/ident])
                                  (:rating/rater-role r)))))
                    (:rating/_job job)))}))

;; =============================================================================
;; Job Filtering
;; =============================================================================

(defn- created-at-millis
  "Get created-at as milliseconds for sorting. Handles js/Date and nil."
  [job]
  (if-let [created (:created-at job)]
    (if (instance? js/Date created)
      (.getTime created)
      0)
    0))

(defn sort-by-newest
  "Sort jobs by created-at descending (newest first)."
  [jobs]
  (sort-by created-at-millis > jobs))

(defn filter-active
  "Filter jobs to only active ones, sorted newest first."
  [jobs]
  (->> jobs
       (filter #(active? (:status %)))
       sort-by-newest))

(defn filter-past
  "Filter jobs to only past ones, sorted newest first."
  [jobs]
  (->> jobs
       (filter #(past? (:status %)))
       sort-by-newest))

;; =============================================================================
;; Cancellation Reasons
;; =============================================================================

(def cancellation-reasons
  "List of cancellation reasons for UI."
  [{:id :change-of-plans :label "Change of plans"}
   {:id :found-another :label "Found another provider"}
   {:id :price-too-high :label "Price too high"}
   {:id :scheduling-conflict :label "Scheduling conflict"}
   {:id :other :label "Other"}])

(defn can-submit-cancellation?
  "Check if cancellation form can be submitted."
  [reason other-reason]
  (boolean
   (and reason
        (or (not= reason :other)
            (seq other-reason)))))

(defn show-before-photos?
  "Should customer see worker's before photos? True from en-route onward."
  [status]
  (contains? #{:en-route :en_route :started :in-progress :finished
               :completed :closed :disputed :resolved} status))

(defn show-after-photos?
  "Should customer see worker's after photos? True from finished onward."
  [status]
  (contains? #{:finished :completed :closed :disputed :resolved} status))
